import { Box, Stack, Typography } from "@mui/material";

import { IFailureEvent } from "../contracts/home.types";
import { formatFederalTaxId } from "../format";
import { formatSaoPauloDateTime } from "../dates";
import { HOME_TOKENS } from "./tokens";

/**
 * Rótulo textual do estado (Achado A, review final v2.1, 2026-08-20). Antes
 * disto, a única coisa na linha que dizia "isto é uma falha" era a cor (régua
 * + tingimento em `error.main`) — e a terceira sublinha, que diz "falhou em
 * <task>", só existe quando `event.taskId` chega preenchido, coisa que o
 * produtor atual (`failure-events.ts`, `taskId: null` fixo) nunca faz. Ou
 * seja: em produção a linha mostrava atividade, dag, hora, cliente e CNPJ, e
 * NADA que dissesse que falhou, enquanto as linhas saudáveis já têm ícone com
 * `aria-label` e texto ("Em dia", "Parado há N dias"). O estado mais
 * importante era o menos rotulado.
 *
 * Vocabulário reaproveitado, nada novo: "falhou" é a palavra que a própria
 * terceira sublinha usa, e "Exige a Conducco" é o nome deste estado no
 * cabeçalho do setor e na opção do filtro de situação. Forma
 * `Estado — qualificador`, a mesma de `HealthyRow` ("Parado — nunca
 * entregou"). Por ser texto dentro do `<button>`, entra no nome acessível da
 * linha de graça.
 */
const ROTULO_ESTADO = "Falhou — exige a Conducco";

/**
 * `#rrggbb` + alpha em hexa. Existia inline no `sx`; virou função ao ganhar o
 * segundo uso (base e hover, Achado E) — a conta é a mesma, o valor de alpha
 * vem sempre de `HOME_TOKENS`.
 */
function tingimentoDeAlarme(errorMain: string, alpha: number): string {
  return `${errorMain}${Math.round(alpha * 255).toString(16).padStart(2, "0")}`;
}

const CONSEQUENCIA: Record<string, string> = {
  NAO_PROCESSOU: "não processou",
  PROCESSOU_LOG_FALHOU: "processou; falhou só ao registrar o fim",
  FALHA_DE_API: "backend de autenticação fora",
};

interface IConduccoRowProps {
  event: IFailureEvent;
  onOpen: (event: IFailureEvent) => void;
}

/**
 * A linha tall do setor "Exige a Conducco" (T8). Única cor de alarme da
 * tela — régua de 3px e tingimento fracos em `error.main`, tirados de
 * `HOME_TOKENS`, nunca soltos aqui.
 *
 * Sublinhas: (0) rótulo textual do estado (`ROTULO_ESTADO`, sempre presente —
 * ver nota acima); (1) atividade + `dagName` monoespaçado, hora à direita;
 * (2) cliente + CNPJ formatado; (3) consequência + task, só quando `taskId`
 * chega preenchido. O backend manda `taskId`/`consequence` como `null` até
 * uma task futura ligar o Airflow — a linha degrada e não inventa texto
 * nenhum, mas nunca deixa de dizer que falhou.
 */
export function ConduccoRow({ event, onOpen }: IConduccoRowProps) {
  const rotuloCliente = `${event.buyerName} · ${formatFederalTaxId(event.federalTaxId)}`;

  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(event)}
      sx={(theme) => ({
        display: "block",
        width: "100%",
        textAlign: "left",
        font: "inherit",
        color: "inherit",
        cursor: "pointer",
        border: 0,
        borderLeft: `${HOME_TOKENS.row.railWidth}px solid`,
        borderColor: HOME_TOKENS.state.CONDUCCO.rail,
        bgcolor: tingimentoDeAlarme(theme.palette.error.main, HOME_TOKENS.state.CONDUCCO.tint),
        minHeight: HOME_TOKENS.row.minHeight,
        px: 1.5,
        py: 1,
        mb: `${HOME_TOKENS.space.betweenRows}px`,
        transition: `background-color ${HOME_TOKENS.motion.hoverMs}ms`,
        /**
         * Achado E (review final v2.1, 2026-08-20): era
         * `bgcolor: "action.hover"`, cinza neutro, que SUBSTITUÍA o tingimento
         * de alarme — o hover apagava o alarme. Hover é eixo de acento e não
         * manda no eixo de alarme: aqui ele só aprofunda o mesmo `error.main`.
         */
        "&:hover": { bgcolor: tingimentoDeAlarme(theme.palette.error.main, HOME_TOKENS.state.CONDUCCO.tintHover) },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: HOME_TOKENS.accent,
          outlineOffset: 2,
        },
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
      })}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        color={HOME_TOKENS.state.CONDUCCO.color}
        noWrap
        title={ROTULO_ESTADO}
        sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}
      >
        {ROTULO_ESTADO}
      </Typography>

      <Stack
        direction="row"
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: `${HOME_TOKENS.space.withinRow}px` }}
      >
        <Typography variant="body2" fontWeight={600} noWrap sx={{ display: "flex", alignItems: "baseline", gap: 1, minWidth: 0 }}>
          {/* Achado E: `title` onde a elipse acontece — mesmo padrão das colunas de `HealthyRow`. */}
          <Box component="span" title={event.activityName} sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {event.activityName}
          </Box>
          {event.dagName && (
            <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 400, color: "text.secondary" }}>
              {event.dagName}
            </Box>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
          {formatSaoPauloDateTime(event.occurredAt)}
        </Typography>
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        title={rotuloCliente}
        sx={{ mb: event.taskId ? `${HOME_TOKENS.space.withinRow}px` : 0 }}
      >
        {rotuloCliente}
      </Typography>

      {event.taskId && (
        <Typography variant="body2">
          {event.consequence ? `${CONSEQUENCIA[event.consequence] ?? event.consequence} — ` : ""}
          falhou em <Box component="span" sx={{ fontFamily: "monospace" }}>{event.taskId}</Box>
        </Typography>
      )}
    </Box>
  );
}
